import {
  Button,
  Flex,
  Heading,
  IconButton,
  IconCopyLine,
  IconInfoSolid,
  InlineList,
  List,
  Pill,
  Text,
  Tooltip,
  View,
} from "@instructure/ui";
import { useContext, useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { Link, useLocation } from "react-router";
import { ExternalService } from "../../../../services/ExternalService";
import { Card } from "../../../Card";
import {
  ERROR,
  SUCCESS,
  useNotification,
} from "../../../CustomHooks/NotificationProvider";
import ConfirmedModal from "../../Rater/ConfirmedModal";
import AssessmentChart from "./Displays/AssessmentChart";
import Findings from "./Displays/Findings";
import Recommendations from "./Displays/Recommendations";
import ResultsSkeleton from "./ResultsSkeleton";

import { UserDetailContext } from "../../../../utils/Context";
import { waitForExpectedResult } from "../../../../utils/Timer";
import {
  FAIL,
  GOOD,
  useStatus,
  WORKING,
} from "../../../CustomHooks/StatusProvider";
import RichTextViewer from "../../../RichTextArea/RichTextViewer";
import {
  CANVAS,
  emailIsInList,
  EMBEDDED,
  EXTERNAL,
  QUALITATIVE,
} from "../../Plan/utils";
import ApproverNotes from "./ApproverNotes";
import CanvasJuriedAssessmentChart from "./Displays/CanvasJuriedAssessmentChart";
import EmbeddedDashboardResults from "./Displays/EmbeddedDashboardResults";
import ExternalJuriedAssessmentChart from "./Displays/ExternalJuriedAssessmentChart";
import FlexibleAssessmentChart from "./Displays/FlexibleAssessmentChart/FlexibleAssessmentChart";
import QualitativeMeasure from "./Displays/QualitativeMeasure/QualitativeMeasure";
import FilterTray from "./FilterTray";
import "./tableau.css";

const H2_THEME_OVERRIDE = { h2FontWeight: 700 };

const Results = () => {
  const { t } = useTranslation();
  const userDetails = useContext(UserDetailContext);
  const showNotification = useNotification();
  const { updateStatus } = useStatus();
  const location = useLocation();
  const bottomRef = useRef();
  const topRef = useRef();
  const [openConfirmModal, setOpenConfirmModal] = useState(
    !location.state?.user
  );
  const [user, setUser] = useState(location.state?.user || "");
  const [header, setHeader] = useState("");
  const [mission, setMission] = useState("");
  const [plan, setPlan] = useState(null);
  const [approvalNotes, setApprovalNotes] = useState({});
  const [cycle, setCycle] = useState(null);
  const [loading, setLoading] = useState(true);
  const [featureFlags, setFeatureFlags] = useState([]);
  const [canvasTerms, setCanvasTerms] = useState([]);
  const [insightsTerms, setInsightsTerms] = useState([]);

  const [filters, setFilters] = useState({
    mode: "term",
    masteryResults: "true",
  });
  const [prevFilters, setPrevFilters] = useState({
    mode: "term",
    masteryResults: "true",
  });
  const [triggerMap, setTriggerMap] = useState(true);

  const publicCode = location.state.public_share_id ?? null;
  const [shareUrl, setShareUrl] = useState(null);

  const finalizeFlag =
    featureFlags.find((f) => f.name === "finalize_results_flag")?.value ===
    "TRUE";

  useEffect(() => {
    const fetchPageData = async () => {
      if (user) {
        try {
          const headerPromise = ExternalService.getFileContent(
            "response.html",
            "header",
            location.state.plan_id
          );
          const missionPromise = ExternalService.getFileContent(
            "response.html",
            "mission",
            location.state.plan_id
          );
          const planPromise = ExternalService.getAssessmentPlanById(
            location.state.plan_id
          );
          const cyclesPromise = ExternalService.getCycles();
          const evidencePromise = ExternalService.getAssignmentsByPlanId(
            location.state.plan_id
          );
          const featureFlagsPromise = ExternalService.getFeatureFlags();
          const canvasTermsPromise = ExternalService.getTerms();
          const insightsTermsPromise = ExternalService.getInsightsTerms();

          const [
            header,
            mission,
            plan,
            cycles,
            evidence,
            flags,
            canvasTerms,
            insightsTerms,
          ] = await Promise.all([
            headerPromise,
            missionPromise,
            planPromise,
            cyclesPromise,
            evidencePromise,
            featureFlagsPromise,
            canvasTermsPromise,
            insightsTermsPromise,
          ]);
          setHeader(header.content);
          setMission(mission.content);
          setFeatureFlags(flags);
          setCanvasTerms(canvasTerms);
          setInsightsTerms(insightsTerms);

          const noteObj = await ExternalService.getApprovalNotesByPlanId(
            location.state.plan_id
          );
          if (noteObj) {
            setApprovalNotes(noteObj);
          }

          const cycle = cycles.find((cycle) => cycle.id === plan.cycle.id);
          setCycle(cycle);

          plan.reviewers = plan.reviewers.replace(/,/g, ", ");
          plan.sections = plan.sections.sort((a, b) => a.sequence - b.sequence);

          const updatedSections = await Promise.all(
            plan.sections.map(async (section) => {
              section.reviewers = section.reviewers.replace(/,/g, ", ");

              const updatedAssessments = await Promise.all(
                section.assessments.map(async (measure) => {
                  const updatedMeasure = { ...measure };

                  if (measure.assessment_type !== "flexible") {
                    const description = await ExternalService.getFileContent(
                      "response.html",
                      "assignmentDescription",
                      measure.id
                    );
                    updatedMeasure.description = description.content;
                  }

                  const assignment_evidence = evidence.filter(
                    (assignment_evidence) =>
                      assignment_evidence.assessment_id === measure.id
                  );

                  updatedMeasure.assignment_evidence = assignment_evidence
                    .filter(
                      (evidence) =>
                        !isNaN(evidence.id) ||
                        (isNaN(evidence.id) && measure.purl_type !== "juried")
                    )
                    .map((evidence) => evidence.id);

                  updatedMeasure.ja_assignment_evidence = assignment_evidence
                    .filter((evidence) => isNaN(evidence.id))
                    .map((evidence) => evidence.id);

                  updatedMeasure.type =
                    measure.assessment_type === EXTERNAL ? EXTERNAL : CANVAS;

                  return updatedMeasure;
                })
              );

              const updatedQualitativeMeasures =
                section.qualitative_measures.map((measure) => ({
                  ...measure,
                  assignees: measure.assignees.replace(/,/g, ", "),
                  type: QUALITATIVE,
                }));

              const updatedEmbeddedDatalinks = section.embedded_datalinks.map(
                (measure) => ({ ...measure, type: EMBEDDED })
              );

              // Combine and sort measures
              const updatedMeasures = [
                ...updatedAssessments,
                ...updatedQualitativeMeasures,
                ...updatedEmbeddedDatalinks,
              ].sort((a, b) => a.sequence - b.sequence);

              return { ...section, measures: updatedMeasures };
            })
          );

          plan.sections = updatedSections;

          setPlan(plan);
          setLoading(false);
          setOpenConfirmModal(false);
        } catch {
          showNotification(
            t("notifications.general.fetchError", {
              obj: t("assessment.assessmentPlan"),
            }),
            ERROR
          );
        }
      }
    };
    fetchPageData();
  }, [user, location, showNotification]);

  const hasQualMeasurePermission = (measure) => {
    // If approved and the approver (<--- double check this)
    if (
      plan.results_purl.results_approved &&
      user.toLowerCase() === plan.results_purl.approvers.toLowerCase()
    ) {
      return true;
    }
    // If not approved and are assigned to measure
    if (
      !plan.results_purl.results_approved &&
      measure.assignees.toLowerCase().includes(user.toLowerCase())
    ) {
      return true;
    }
    return false;
  };

  const handleApprove = async (uuid) => {
    if (uuid === plan.id) {
      setPlan((prevPlan) => ({
        ...prevPlan,
        results_purl: { ...prevPlan.results_purl, results_approved: true },
      }));
    } else {
      setPlan((prevPlan) => {
        const newSections = prevPlan.sections.map((section) => {
          if (section.id === uuid) {
            return { ...section, results_approved: true };
          }
          return section;
        });
        return { ...prevPlan, sections: newSections };
      });
    }
    const noteObj = await ExternalService.getApprovalNotesByPlanId(
      location.state.plan_id
    );
    setApprovalNotes(noteObj);
  };

  const handleUpdateNote = (uuid, newNote) => {
    setApprovalNotes({
      ...approvalNotes,
      [uuid]: { ...approvalNotes[uuid], note: newNote },
    });
  };

  const getFlexibleAssessmentSubjects = (plan) => {
    const subjectMap = {};
    plan.sections.forEach((section) => {
      section.measures.forEach((measure) => {
        if (measure.assessment_type === "flexible") {
          measure.subjects.forEach((subject) => {
            subjectMap[subject.id] = subject.title;
          });
        }
      });
    });
    return subjectMap;
  };

  const handlePublish = () => {
    const subjectMap = getFlexibleAssessmentSubjects(plan);
    showNotification(t("results.resultsPublishing"));
    ExternalService.publishExternalResults(plan.id, user, subjectMap).then(
      (task) => {
        if (task.length === 0) {
          updateStatus({
            service: {
              name: t("results.publishStatus"),
              status: GOOD,
              progress: 100,
            },
          });
          setShareUrl(task.obj.share_url);
        } else {
          waitForExpectedResult(
            () => ExternalService.getTaskById(task.ddl_id),
            (task) => {
              const progress = parseInt(
                (task.counter / task.obj.total_results) * 100
              );
              updateStatus({
                service: {
                  name: t("results.publishStatus"),
                  status: progress === 100 ? GOOD : WORKING,
                  progress: progress,
                },
              });
              return task.status === "done" || task.status === "error";
            },
            processPublishTask,
            () => {
              showNotification(
                t("notifications.assessmentResults.publishError"),
                ERROR
              );
              updateStatus({
                service: { name: t("results.publishStatus"), status: FAIL },
              });
            },
            30,
            3000
          );
        }
      }
    );
  };

  const processPublishTask = (task) => {
    if (task.status === "error") {
      showNotification(
        t("notifications.assessmentResults.publishError"),
        ERROR
      );
    } else if (task.status === "done") {
      showNotification(t("results.publishedAlert"), SUCCESS);
      setShareUrl(task.obj.share_url);
    }
  };

  return (
    <>
      {!openConfirmModal &&
        (!loading ? (
          <View as="div" background="primary" position="relative">
            <div ref={topRef}>
              <View as="div" borderWidth="0 0 small 0" margin="0 0 small 0">
                {userDetails.ddl_id ? (
                  <Flex wrap="wrap" justifyItems="space-between" gap="x-small">
                    <Flex.Item>
                      <InlineList delimiter="arrow" size="large">
                        <InlineList.Item>
                          <Text size="x-large">
                            <Link
                              state={{ planId: location.state.plan_id }}
                              to="/"
                            >
                              {t("navigation.dashboard")}
                            </Link>
                          </Text>
                        </InlineList.Item>
                        <InlineList.Item>
                          <Text size="x-large">{plan.name || cycle.name}</Text>
                        </InlineList.Item>
                      </InlineList>
                    </Flex.Item>
                    <Flex.Item>
                      <Text>
                        {t("cyclesAndGoals.cycle")}: {cycle.name}
                      </Text>
                    </Flex.Item>
                  </Flex>
                ) : (
                  <Flex wrap="wrap" justifyItems="space-between" gap="x-small">
                    <Flex.Item>
                      <Heading level="h2" as="h1">
                        {plan.name || cycle.name}
                      </Heading>
                    </Flex.Item>
                    <Flex.Item>
                      <Text>
                        {t("cyclesAndGoals.cycle")}: {cycle.name}
                      </Text>
                    </Flex.Item>
                  </Flex>
                )}
              </View>
            </div>
            <Flex gap="small" margin="small 0 0 0" wrap="wrap">
              <Flex.Item>
                <FilterTray
                  filters={filters}
                  setFilters={setFilters}
                  prevFilters={prevFilters}
                  setPrevFilters={setPrevFilters}
                  setTriggerMap={setTriggerMap}
                />
              </Flex.Item>
              {location.state.plan_id &&
                (userDetails.ddl_id ||
                  emailIsInList(plan?.reviewers?.split(","), user) ||
                  user.toLowerCase() ===
                    plan?.results_purl?.approvers?.toLowerCase()) && (
                  <Flex.Item>
                    <Link
                      to={`/assessment-status/${location.state.plan_id}`}
                      state={{
                        plan,
                        resultsState: { ...location.state, user },
                      }}
                    >
                      <Button size="small">
                        {t("assessmentPlanningDashboard.trackPlanProgress")}
                      </Button>
                    </Link>
                  </Flex.Item>
                )}
              <Flex.Item>
                <Button
                  size="small"
                  onClick={() => bottomRef.current.scrollIntoView()}
                >
                  {t("results.planFindingsAndRecommendations")}
                </Button>
              </Flex.Item>
              <Flex.Item textAlign="end" shouldGrow>
                <Text fontStyle="italic">
                  {t("assessment.approver")}: {plan.results_purl.approvers}
                </Text>
              </Flex.Item>
              <Flex.Item>
                {plan.results_purl.results_approved ? (
                  <Pill color="success">{t("results.approved")}</Pill>
                ) : (
                  <Pill color="warning">{t("results.awaitingApproval")}</Pill>
                )}
              </Flex.Item>
              {user.toLowerCase() ===
                plan.results_purl.approvers.toLowerCase() &&
                plan.results_purl.results_approved && (
                  <Flex.Item>
                    <Button size="small" onClick={handlePublish}>
                      {t("results.publish")}
                    </Button>
                  </Flex.Item>
                )}
              {user.toLowerCase() ===
                plan.results_purl.approvers.toLowerCase() && (
                <Flex.Item>
                  <Flex gap="small">
                    <Flex.Item>
                      <ApproverNotes
                        context="plan"
                        uuid={plan.id}
                        noteObj={approvalNotes[plan.id]}
                        btnLabel={t("finalizeCourse.finalize")}
                        isApproved={plan.results_purl.results_approved}
                        handleApprove={handleApprove}
                        handleUpdate={handleUpdateNote}
                        disabled={
                          Object.keys(approvalNotes).length <
                          plan.sections.length
                        }
                      />
                    </Flex.Item>
                    {Object.keys(approvalNotes).length <
                      plan.sections.length && (
                      <Flex.Item>
                        <Tooltip renderTip={t("results.approveWarning")}>
                          <IconInfoSolid color="warning" />
                        </Tooltip>
                      </Flex.Item>
                    )}
                  </Flex>
                </Flex.Item>
              )}
              {(!!publicCode || !!shareUrl) && (
                <Flex.Item size="100%">
                  <BuildPublicPath code={publicCode} shareUrl={shareUrl} />
                </Flex.Item>
              )}
              {plan.results_purl.results_approved &&
                approvalNotes[plan.id]?.note && (
                  <Flex.Item size="100%">
                    <Card>
                      <Text weight="bold">{t("results.approverNotes")}</Text>
                      <br />
                      <Text>{approvalNotes[plan.id].note}</Text>
                    </Card>
                  </Flex.Item>
                )}
            </Flex>
            <Flex gap="large" wrap="wrap" margin="small 0 0 0">
              {header && (
                <Flex.Item margin="small 0 0 0">
                  <RichTextViewer
                    ariaLabel={t("assessment.header")}
                    value={header}
                  />
                </Flex.Item>
              )}
              {mission !== "" && (
                <Flex.Item size="100%">
                  <Heading
                    level="h2"
                    border="bottom"
                    themeOverride={H2_THEME_OVERRIDE}
                  >
                    {t("assessment.mission")}:
                  </Heading>

                  <View display="block" margin="small medium 0 large">
                    <RichTextViewer
                      value={mission}
                      ariaLabel={t("assessment.mission")}
                    />
                  </View>
                </Flex.Item>
              )}
              {plan.include_goals_objs && (
                <Flex.Item size="100%">
                  <Heading
                    level="h2"
                    border="bottom"
                    themeOverride={H2_THEME_OVERRIDE}
                  >
                    {t("dashboard.goalsAndObjectives")}:
                  </Heading>
                  <List isUnstyled margin="small 0 0 large">
                    {cycle.goals.map((goal) => (
                      <List.Item key={goal.id} margin="0">
                        <Text weight="bold">{goal.description}</Text>
                        <List>
                          {goal.objectives.map((objective) => (
                            <List.Item key={objective.id}>
                              {objective.description}
                            </List.Item>
                          ))}
                        </List>
                      </List.Item>
                    ))}
                  </List>
                </Flex.Item>
              )}
              {plan.sections.map((section) => (
                <Flex.Item key={section.id} size="100%">
                  <Flex wrap="wrap" gap="small">
                    <Flex.Item shouldGrow>
                      <div style={{ borderBottom: "1px solid #ccc" }}>
                        <Flex gap="small" wrap="wrap">
                          <Flex.Item>
                            <Heading
                              level="h2"
                              themeOverride={H2_THEME_OVERRIDE}
                            >
                              {section.name}
                            </Heading>
                          </Flex.Item>
                          <Flex.Item
                            shouldGrow
                            align="end"
                            padding="0 0 xx-small 0"
                          >
                            <Text fontStyle="italic">
                              {t("assessment.sections.approver")}:{" "}
                              {section.approvers}
                            </Text>
                          </Flex.Item>
                          <Flex.Item>
                            <Flex gap="small" wrap="wrap">
                              <Flex.Item>
                                {section.results_approved ? (
                                  <Pill color="success">
                                    {t("results.approved")}
                                  </Pill>
                                ) : (
                                  <Pill color="warning">
                                    {t("results.awaitingApproval")}
                                  </Pill>
                                )}
                              </Flex.Item>
                              {section.approvers
                                .toLowerCase()
                                .includes(user.toLowerCase()) && (
                                <Flex.Item>
                                  <ApproverNotes
                                    context="section"
                                    uuid={section.id}
                                    noteObj={approvalNotes[section.id]}
                                    btnLabel={t("analyze.approve")}
                                    isApproved={section.results_approved}
                                    handleApprove={handleApprove}
                                    handleUpdate={handleUpdateNote}
                                  />
                                </Flex.Item>
                              )}
                            </Flex>
                          </Flex.Item>
                        </Flex>
                      </div>
                    </Flex.Item>

                    {section.results_approved &&
                      approvalNotes[section.id]?.note && (
                        <Flex.Item size="100%" padding="0 0 0 large">
                          <Card>
                            <Text weight="bold">
                              {t("results.approverNotes")}
                            </Text>
                            <br />
                            <Text>{approvalNotes[section.id].note}</Text>
                          </Card>
                        </Flex.Item>
                      )}
                    <Flex.Item size="100%" padding="0 0 0 large">
                      <RichTextViewer
                        value={section.description}
                        ariaLabel={t("assessment.sections.sectionDesc")}
                      />
                    </Flex.Item>
                    {section.measures.map((measure, measureKey) => {
                      // There can be only 1 plan approver
                      // There can be only 1 approver per section
                      const canPersist =
                        user.toLowerCase() ===
                          plan.results_purl.approvers.toLowerCase() ||
                        user.toLowerCase() === section.approvers.toLowerCase();

                      return (
                        <Flex.Item
                          key={measureKey}
                          size="100%"
                          padding="0 0 0 large"
                        >
                          {(measure.type === CANVAS ||
                            measure.type === EXTERNAL) && (
                            <>
                              {measure.assessment_type !== "flexible" &&
                                !measure.collecting_evidence && (
                                  <AssessmentChart
                                    measure={measure}
                                    filters={filters}
                                    triggerMap={triggerMap}
                                    canPersist={canPersist}
                                    approverEmail={user}
                                    canvasTerms={canvasTerms}
                                    insightsTerms={insightsTerms}
                                  />
                                )}
                              {measure.type === CANVAS &&
                                measure.assessment_type === "flexible" && (
                                  <FlexibleAssessmentChart
                                    measure={measure}
                                    filters={filters}
                                    prevFilters={prevFilters}
                                    triggerMap={triggerMap}
                                    canPersist={canPersist}
                                    approverEmail={user}
                                    finalizeFlag={finalizeFlag}
                                  />
                                )}
                              {measure.type === CANVAS &&
                                measure.collecting_evidence && (
                                  <CanvasJuriedAssessmentChart
                                    measure={measure}
                                    filters={filters}
                                    triggerMap={triggerMap}
                                    canPersist={canPersist}
                                    approverEmail={user}
                                    canvasTerms={canvasTerms}
                                    insightsTerms={insightsTerms}
                                  />
                                )}
                              {measure.type === EXTERNAL &&
                                measure.collecting_evidence && (
                                  <ExternalJuriedAssessmentChart
                                    measure={measure}
                                    filters={filters}
                                    triggerMap={triggerMap}
                                    canPersist={canPersist}
                                    approverEmail={user}
                                    canvasTerms={canvasTerms}
                                    insightsTerms={insightsTerms}
                                  />
                                )}
                            </>
                          )}
                          {measure.type === QUALITATIVE && (
                            <QualitativeMeasure
                              measure={measure}
                              permission={hasQualMeasurePermission(measure)}
                              userEmail={user}
                            />
                          )}
                          {measure.type === EMBEDDED && (
                            <EmbeddedDashboardResults
                              measure={measure}
                              planId={plan.id}
                              sectionId={section.id}
                            />
                          )}
                        </Flex.Item>
                      );
                    })}
                    <Flex.Item size="100%" padding="0 0 0 large">
                      <Findings
                        id={section.id}
                        label={
                          <Flex wrap="wrap" gap="small">
                            <Flex.Item>
                              <Heading level="h3">
                                {t("results.sectionFindings")}
                              </Heading>
                            </Flex.Item>
                            <Flex.Item padding="xxx-small 0 0 0">
                              <Text fontStyle="italic">
                                {t("measures.assignedTo")}: {section.reviewers}
                              </Text>
                            </Flex.Item>
                          </Flex>
                        }
                        permission={emailIsInList(
                          section.reviewers?.split(","),
                          user
                        )}
                      />
                    </Flex.Item>
                    <Flex.Item size="100%" padding="0 0 0 large">
                      <Recommendations
                        id={section.id}
                        label={
                          <Flex wrap="wrap" gap="small">
                            <Flex.Item>
                              <Heading level="h3">
                                {t("results.sectionRecommendations")}
                              </Heading>
                            </Flex.Item>
                            <Flex.Item padding="xxx-small 0 0 0">
                              <Text fontStyle="italic">
                                {t("measures.assignedTo")}: {section.reviewers}
                              </Text>
                            </Flex.Item>
                          </Flex>
                        }
                        permission={emailIsInList(
                          section.reviewers?.split(","),
                          user
                        )}
                      />
                    </Flex.Item>
                  </Flex>
                </Flex.Item>
              ))}
            </Flex>
            <div
              ref={bottomRef}
              style={{
                borderTop: "1px dashed #ccc",
                marginBottom: 10,
                marginTop: 20,
              }}
            />
            <Card style={{ backgroundColor: "#F1F1F1", height: "auto" }}>
              <Flex wrap="wrap" gap="small">
                <Flex.Item size="100%">
                  <Findings
                    id={cycle.id}
                    label={
                      <Flex wrap="wrap" gap="small">
                        <Flex.Item>
                          <Heading level="h3">
                            {t("results.cycleFindings")}
                          </Heading>
                        </Flex.Item>
                        <Flex.Item padding="xxx-small 0 0 0">
                          <Text fontStyle="italic">
                            {t("measures.assignedTo")}: {plan.reviewers}
                          </Text>
                        </Flex.Item>
                      </Flex>
                    }
                    permission={emailIsInList(plan.reviewers?.split(","), user)}
                  />
                </Flex.Item>
                <Flex.Item size="100%">
                  <Recommendations
                    id={cycle.id}
                    label={
                      <Flex wrap="wrap" gap="small">
                        <Flex.Item>
                          <Heading level="h3">
                            {t("results.cycleRecommendations")}
                          </Heading>
                        </Flex.Item>
                        <Flex.Item padding="xxx-small 0 0 0">
                          <Text fontStyle="italic">
                            {t("measures.assignedTo")}: {plan.reviewers}
                          </Text>
                        </Flex.Item>
                      </Flex>
                    }
                    permission={emailIsInList(plan.reviewers?.split(","), user)}
                  />
                </Flex.Item>
              </Flex>
            </Card>

            <Button
              margin="small 0 0 0"
              size="small"
              onClick={() => topRef.current.scrollIntoView()}
            >
              {t("results.returnToTop")}
            </Button>
          </View>
        ) : (
          <ResultsSkeleton />
        ))}
      <ConfirmedModal
        open={openConfirmModal}
        setUser={setUser}
        shareCode={location.state.share_id}
      />
    </>
  );
};

export default Results;

const BuildPublicPath = ({ code, shareUrl }) => {
  const { t } = useTranslation();
  const [copyText, setCopyText] = useState(t("results.copyToClipboard"));
  const tenantID = window.location.hostname.split(".")[0];
  const stage = window.location.hostname.split("/")[0];
  const publicUrl =
    shareUrl ?? `${stage}/public/public-results/${tenantID}/${code}`;

  return (
    <Card style={{ marginTop: 20 }}>
      <Text>{t("results.resultsPublic")} </Text>
      <Text id="share-url" weight="bold">
        {publicUrl}
      </Text>
      <Tooltip
        renderTip={copyText}
        on={["hover", "focus"]}
        onHideContent={() => setCopyText(t("results.copyToClipboard"))}
      >
        <IconButton
          color="primary"
          renderIcon={IconCopyLine}
          screenReaderLabel={t("results.copyToClipboard")}
          size="small"
          margin="0 0 0 x-small"
          withBorder={false}
          withBackground={false}
          onClick={() => {
            navigator.clipboard.writeText(publicUrl);
            setCopyText(t("results.copiedToClipboard"));
          }}
        />
      </Tooltip>
    </Card>
  );
};
